/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 16;

  /**
   * Adds a product to the recently viewed products list.
   * Accepts either a plain ID string (legacy) or an object { id, handle }.
   * @param {string|{id: string, handle: string}} product - The product ID or { id, handle } object.
   */
  static addProduct(product) {
    let viewedProducts = this.getProducts();
    const id = typeof product === 'object' ? product.id : product;
    const handle = typeof product === 'object' ? product.handle : null;

    // Remove existing entry for this product
    viewedProducts = viewedProducts.filter((entry) => {
      const entryId = typeof entry === 'object' ? entry.id : entry;
      return entryId !== id;
    });

    // Store as object if handle is provided, otherwise store plain ID (backwards-compatible)
    viewedProducts.unshift(handle ? { id, handle } : id);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {Array<string|{id: string, handle: string}>} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Retrieves product handles from recently viewed, filtering out legacy ID-only entries.
   * @returns {string[]} Array of product handles.
   */
  static getHandles() {
    return this.getProducts()
      .filter((entry) => typeof entry === 'object' && entry.handle)
      .map((entry) => entry.handle);
  }
}
